/** @format */

import { createAsyncThunk } from "@reduxjs/toolkit";
import {
	// Service imports
	fetchAllContributionsAsAdmin,
	fetchContributionsByDateAsAdmin,
	fetchContributionsByUserAsAdmin,
	fetchContributionsByCircleAsAdmin,
	fetchContributionsByCycleAsAdmin,
	fetchFailedContributionsAsAdmin,
	fetchAllActiveCyclesAsAdmin,
	fetchCycleContributionDetailsAsAdmin,
	fetchCycleSessionsBreakdownAsAdmin,
	fetchAllUsersAutoPayStatusAsAdmin,
	fetchUserAutoPayStatusAsAdmin,
	fetchAllCirclesAutoPayStatusAsAdmin,
	fetchCircleAutoPayStatusAsAdmin,
	// Type imports
	PaginationParams,
	ContributionsByDateParams,
	ContributionsByUserParams,
	ContributionsByCircleParams,
	ContributionsByCycleParams,
} from "../services/adminContributionService";

// ==================== Response Types ====================

export interface ContributionReportEntry {
	contributionId: string;
	memberId: string;
	memberName: string;
	memberEmail: string;
	circleId: string;
	circleName: string;
	amount: number;
	amountFormatted: string;
	status: string;
	contributionForDate: string;
	createdAt: Date;
	cycle?: {
		cycleId: string;
		startDate: Date;
		endDate: Date;
		payOutDate: Date;
		status: string;
	};
}

export interface ContributionReportSummary {
	totalContributions: number;
	totalAmount: number;
	totalAmountFormatted: string;
	completedCount: number;
	failedCount: number;
	pendingCount: number;
	averageAmount: number;
}

export interface ContributionReportPagination {
	total: number;
	page: number;
	limit: number;
	pages: number;
}

export interface ActiveCycleInfo {
	cycleId: string;
	circleId: string;
	circleName: string;
	startDate: Date;
	endDate: Date;
	payOutDate: Date;
	status: string;
	memberCount: number;
	contributionsTotal: number;
	contributionsTotalFormatted: string;
	expectedContributionAmount: number;
	contributionFrequency: string;
	contributionDays: string[];
	timezone: string;
	daysRemaining: number;
	progressPercentage: number;
	// Upcoming payout information based on session
	upcomingPayoutDate: Date | null;
	upcomingPayoutRecipientId: string | null;
	upcomingPayoutRecipientName: string | null;
	currentSessionIndex: number;
	totalSessions: number;
}

export interface CycleMemberContributionStatus {
	userId: string;
	name: string;
	email: string;
	isLeader: boolean;
	joinedAt: Date;
	contributionCount: number;
	totalContributed: number;
	totalContributedFormatted: string;
	lastContributionDate: Date | null;
	contributionDates: string[];
	status: "up-to-date" | "behind" | "no-contributions";
}

export interface SessionMemberContribution {
	userId: string;
	name: string;
	email: string;
	isLeader: boolean;
	contributed: boolean;
	contributionAmount: number;
	contributionAmountFormatted: string;
	contributionId?: string;
	contributedAt?: Date;
}

export interface CycleSessionContributions {
	sessionDate: string;
	sessionDateFormatted: string;
	dayOfWeek: string;
	isPast: boolean;
	isToday: boolean;
	isFuture: boolean;
	membersContributed: number;
	membersNotContributed: number;
	totalCollected: number;
	totalCollectedFormatted: string;
	expectedTotal: number;
	expectedTotalFormatted: string;
	completionPercentage: number;
	members: SessionMemberContribution[];
}

export interface UserAutoPayStatus {
	userId: string;
	name: string;
	email: string;
	globalAutoPayEnabled: boolean;
	circleCount: number;
	circles: {
		circleId: string;
		circleName: string;
		isLeader: boolean;
		joinedAt: Date;
	}[];
}

export interface CircleAutoPaySummary {
	circleId: string;
	circleName: string;
	circleImage: string;
	totalMembers: number;
	membersWithAutoPay: number;
	membersWithoutAutoPay: number;
	autoPayPercentage: number;
	hasActiveCycle: boolean;
}

// ==================== Contribution Report Actions ====================

/**
 * Get all contributions with pagination (admin only)
 */
export const getAllContributionsAsAdmin = createAsyncThunk(
	"adminContribution/getAllContributions",
	async (params: PaginationParams | undefined, { rejectWithValue }) => {
		try {
			const response = await fetchAllContributionsAsAdmin(params);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch all contributions",
			);
		}
	},
);

/**
 * Get contributions for a specific date (admin only)
 */
export const getContributionsByDateAsAdmin = createAsyncThunk(
	"adminContribution/getContributionsByDate",
	async (params: ContributionsByDateParams, { rejectWithValue }) => {
		try {
			const response = await fetchContributionsByDateAsAdmin(params);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch contributions by date",
			);
		}
	},
);

/**
 * Get contributions for a specific user (admin only)
 */
export const getContributionsByUserAsAdmin = createAsyncThunk(
	"adminContribution/getContributionsByUser",
	async (params: ContributionsByUserParams, { rejectWithValue }) => {
		try {
			const response = await fetchContributionsByUserAsAdmin(params);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch contributions by user",
			);
		}
	},
);

/**
 * Get contributions for a specific circle (admin only)
 */
export const getContributionsByCircleAsAdmin = createAsyncThunk(
	"adminContribution/getContributionsByCircle",
	async (params: ContributionsByCircleParams, { rejectWithValue }) => {
		try {
			const response = await fetchContributionsByCircleAsAdmin(params);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch contributions by circle",
			);
		}
	},
);

/**
 * Get contributions for a specific cycle (admin only)
 */
export const getContributionsByCycleAsAdmin = createAsyncThunk(
	"adminContribution/getContributionsByCycle",
	async (params: ContributionsByCycleParams, { rejectWithValue }) => {
		try {
			const response = await fetchContributionsByCycleAsAdmin(params);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch contributions by cycle",
			);
		}
	},
);

/**
 * Get all failed contributions (admin only)
 */
export const getFailedContributionsAsAdmin = createAsyncThunk(
	"adminContribution/getFailedContributions",
	async (params: PaginationParams | undefined, { rejectWithValue }) => {
		try {
			const response = await fetchFailedContributionsAsAdmin(params);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch failed contributions",
			);
		}
	},
);

// ==================== Cycle-Based Contribution Tracking Actions ====================

/**
 * Get all active contribution cycles (admin only)
 */
export const getAllActiveCyclesAsAdmin = createAsyncThunk(
	"adminContribution/getAllActiveCycles",
	async (params: PaginationParams | undefined, { rejectWithValue }) => {
		try {
			const response = await fetchAllActiveCyclesAsAdmin(params);
			return response;
		} catch (error: any) {
			return rejectWithValue(error.message || "Failed to fetch active cycles");
		}
	},
);

/**
 * Get detailed member contribution status for a specific cycle (admin only)
 */
export const getCycleContributionDetailsAsAdmin = createAsyncThunk(
	"adminContribution/getCycleContributionDetails",
	async (cycleId: string, { rejectWithValue }) => {
		try {
			const response = await fetchCycleContributionDetailsAsAdmin(cycleId);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch cycle contribution details",
			);
		}
	},
);

/**
 * Get session-by-session breakdown of contributions within a cycle (admin only)
 */
export const getCycleSessionsBreakdownAsAdmin = createAsyncThunk(
	"adminContribution/getCycleSessionsBreakdown",
	async (cycleId: string, { rejectWithValue }) => {
		try {
			const response = await fetchCycleSessionsBreakdownAsAdmin(cycleId);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch cycle sessions breakdown",
			);
		}
	},
);

// ==================== Auto-Pay Status Actions ====================

/**
 * Get all users with their auto-pay status (admin only)
 */
export const getAllUsersAutoPayStatusAsAdmin = createAsyncThunk(
	"adminContribution/getAllUsersAutoPayStatus",
	async (params: PaginationParams | undefined, { rejectWithValue }) => {
		try {
			const response = await fetchAllUsersAutoPayStatusAsAdmin(params);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch users auto-pay status",
			);
		}
	},
);

/**
 * Get auto-pay status for a specific user (admin only)
 */
export const getUserAutoPayStatusAsAdmin = createAsyncThunk(
	"adminContribution/getUserAutoPayStatus",
	async (userId: string, { rejectWithValue }) => {
		try {
			const response = await fetchUserAutoPayStatusAsAdmin(userId);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch user auto-pay status",
			);
		}
	},
);

/**
 * Get all circles with their auto-pay summary (admin only)
 */
export const getAllCirclesAutoPayStatusAsAdmin = createAsyncThunk(
	"adminContribution/getAllCirclesAutoPayStatus",
	async (params: PaginationParams | undefined, { rejectWithValue }) => {
		try {
			const response = await fetchAllCirclesAutoPayStatusAsAdmin(params);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch circles auto-pay status",
			);
		}
	},
);

/**
 * Get auto-pay status for a specific circle (admin only)
 */
export const getCircleAutoPayStatusAsAdmin = createAsyncThunk(
	"adminContribution/getCircleAutoPayStatus",
	async (circleId: string, { rejectWithValue }) => {
		try {
			const response = await fetchCircleAutoPayStatusAsAdmin(circleId);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				error.message || "Failed to fetch circle auto-pay status",
			);
		}
	},
);
